/**
 * ConnectionsStep Component
 *
 * Step for configuring tracker connections, MMP provider, and store types
 * in the landing page creation/edit flow.
 */
import React, { useEffect, useMemo, useState } from "react";

import {
    Alert,
    Box,
    Button,
    Chip,
    FormControl,
    FormHelperText,
    MenuItem,
    Select,
    Skeleton,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";

import { yupResolver } from "@hookform/resolvers/yup";
// Hooks
import useUpdateEffect from "hooks/useUpdateEffect";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { validateOneLink } from "helpers/trackers/validateOneLink";

import { CommonLandingPageSteps } from "../../AddEditLandingPage.types";
// Local imports
import StepContainer from "../../components/StepContainer";
import { renderTemplateInput } from "../LandingPageTemplateSelectStep/LandingPageTemplateSelectStep.helpers";
import {
    MMP_PROVIDER_OPTIONS,
    STORE_TYPE_OPTIONS,
    TEMPLATES_REQUIRING_CUSTOM_SOURCES,
    TEMPLATES_REQUIRING_GAMIFICATION,
} from "./ConnectionsStep.constants";
// Helpers, Constants & Types
import {
    connectionsStepSchema,
    getDefaultFormValues,
} from "./ConnectionsStep.helpers";
import { useMarketplacePrefill } from "./useMarketplacePrefill";

// ============================================================================
// Component
// ============================================================================

export default function ConnectionsStep({
    onSave,
    form,
    selectedTemplatedID,
    trackers,
    templates,
    isStepCompleted,
    appDetails,
    gamificationTemplates,
    setIsStepDirty,
    customSources,
    dispatchGetAppDetails,
    dispatchGetGamificationTemplates,
    dispatchLeadCustomSources,
}) {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    // ========================================================================
    // Form Setup
    // ========================================================================

    const hookForm = useForm({
        mode: "onChange",
        defaultValues: getDefaultFormValues(form),
        resolver: yupResolver(connectionsStepSchema),
    });

    const {
        control,
        handleSubmit,
        reset,
        formState: { errors, isDirty },
        watch,
        setValue,
    } = hookForm;

    // ========================================================================
    // Watched Values
    // ========================================================================

    const selectedAppTypes = useWatch({ control, name: "app_type" });
    const appleIdField = useWatch({ control, name: "applications.apple" });
    const mmpField = useWatch({ control, name: "mmp" });
    const oneLinkField = useWatch({ control, name: "one_link" });
    const selectedTrackerID = useWatch({ control, name: "tracker_id" });

    // OneLink validation state
    const [oneLinkValidation, setOneLinkValidation] = useState({
        isValidating: false,
        result: null,
    });

    // ========================================================================
    // Derived State
    // ========================================================================

    const populatedTracker = useMemo(
        () => trackers?.find((tracker) => tracker?.id === selectedTrackerID),
        [trackers, selectedTrackerID],
    );

    const selectedTemplate = useMemo(
        () =>
            templates?.data?.find(
                (template) => template?.id === selectedTemplatedID,
            ),
        [templates, selectedTemplatedID],
    );

    const isLeadOrFunnelTemplate = TEMPLATES_REQUIRING_CUSTOM_SOURCES.includes(
        selectedTemplate?.name,
    );

    const isGamificationTemplate = TEMPLATES_REQUIRING_GAMIFICATION.includes(
        selectedTemplate?.name,
    );

    // The OneLink only drives store redirections, so it is validated only when
    // a store type is selected. A web/whatsapp only setup skips it entirely.
    const validatesOneLink = !!(
        selectedAppTypes?.includes("android") ||
        selectedAppTypes?.includes("apple")
    );

    // ========================================================================
    // Marketplace Prefill Hook
    // ========================================================================

    const { prefilledValues } = useMarketplacePrefill({
        selectedTrackerID,
        mmpField,
        selectedAppTypes,
        populatedTracker,
        watch,
        setValue,
    });

    // ========================================================================
    // Effects
    // ========================================================================

    // Sync form with external form prop
    useEffect(() => {
        reset(form);
    }, [form, reset]);

    // Handle app types changes - preserve prefilled values during reset
    useUpdateEffect(() => {
        const currentApple = watch("applications.apple") || prefilledValues.ios;
        const currentAndroid =
            watch("applications.android") || prefilledValues.android;

        reset({
            tracker_id: watch("tracker_id"),
            mmp: watch("mmp"),
            app_type: watch("app_type"),
            one_link: watch("one_link"),
            applications: {
                ...(selectedAppTypes?.includes("apple") && {
                    apple: currentApple,
                }),
                ...(selectedAppTypes?.includes("android") && {
                    android: currentAndroid,
                }),
                ...(selectedAppTypes?.includes("whatsapp") && {
                    phone_number: watch("applications.phone_number"),
                }),
            },
            ...(selectedAppTypes?.includes("web") && {
                target_link: watch("target_link"),
            }),
        });
    }, [selectedAppTypes?.length]);

    // Handle MMP changes - toggle one_link field
    useUpdateEffect(() => {
        reset({
            ...watch(),
            ...(mmpField === "adjust" || mmpField === "appsflyer"
                ? { one_link: watch("one_link") }
                : { one_link: undefined }),
        });
    }, [mmpField]);

    // Track dirty state
    useUpdateEffect(() => {
        setIsStepDirty(isDirty);
    }, [isDirty]);

    // OneLink validation effect with debounce
    useEffect(() => {
        // Reset validation state if MMP is not adjust, oneLink is empty or no
        // store type that the OneLink would redirect to is selected
        if (mmpField !== "adjust" || !oneLinkField || !validatesOneLink) {
            setOneLinkValidation({
                isValidating: false,
                result: null,
            });
            return;
        }

        const timeoutId = setTimeout(async () => {
            setOneLinkValidation({
                isValidating: true,
                result: null,
            });

            const result = await validateOneLink(oneLinkField, mmpField);

            setOneLinkValidation({
                isValidating: false,
                result: result.data,
            });
        }, 800);

        return () => clearTimeout(timeoutId);
    }, [oneLinkField, mmpField, validatesOneLink]);

    // ========================================================================
    // Handlers
    // ========================================================================

    /**
     * Cleans form data before saving:
     * - Removes null/undefined/empty values from applications
     * - Only includes applications for selected store types
     */
    const cleanFormData = (data) => {
        const { applications, app_type, ...rest } = data;

        // Extract and normalize values
        const appleKey = applications?.apple?.trim();
        const androidKey = applications?.android?.trim();
        const whatsappNumber = applications?.phone_number?.trim();

        const isApple = app_type?.includes("apple");
        const isAndroid = app_type?.includes("android");
        const isWhatsapp = app_type?.includes("whatsapp");

        // Build clean applications object only for selected store types
        const cleanApplications = {};

        // Handler pattern for cleaner conditional logic
        const handlers = [
            {
                predicate: () => isApple && appleKey,
                handler: () => {
                    cleanApplications.apple = appleKey;
                },
            },
            {
                predicate: () => isAndroid && androidKey,
                handler: () => {
                    cleanApplications.android = androidKey;
                },
            },
            {
                predicate: () => isWhatsapp && whatsappNumber,
                handler: () => {
                    cleanApplications.phone_number = whatsappNumber;
                },
            },
        ];

        // Execute handlers that match their predicates
        handlers.forEach(({ predicate, handler }) => {
            if (predicate()) {
                handler();
            }
        });

        return {
            ...rest,
            app_type,
            // Only include applications if there are any valid entries
            ...(Object.keys(cleanApplications).length > 0 && {
                applications: cleanApplications,
            }),
        };
    };

    const onSubmit = () => {
        const rawData = watch();
        const cleanedData = cleanFormData(rawData);

        onSave({
            stepName: CommonLandingPageSteps.CONNECTIONS,
            data: cleanedData,
        });
    };

    // ========================================================================
    // Render Helpers
    // ========================================================================

    // Check if trackers exist
    const hasTrackers = useMemo(
        () => trackers && trackers.length > 0,
        [trackers],
    );

    const renderTrackerSelect = () => {
        if (!hasTrackers) {
            return (
                <Box
                    sx={{
                        p: { xs: 2, md: 3 },
                        backgroundColor: "#F8F9FA",
                        borderRadius: 2,
                        border: "1px solid #EDF0F6",
                    }}
                >
                    <Stack gap={2} alignItems="center">
                        <Alert
                            severity={errors.tracker_id ? "error" : "info"}
                            sx={{ width: "100%" }}
                        >
                            <Typography variant="body2">
                                {errors.tracker_id?.message ||
                                    "No tracker found."}
                            </Typography>
                        </Alert>
                        <Button
                            color="primary"
                            variant="contained"
                            onClick={() => navigate("/wizard")}
                        >
                            Add Tracker
                        </Button>
                    </Stack>
                </Box>
            );
        }

        return (
            <Box>
                <FormControl fullWidth error={!!errors.tracker_id}>
                    <Controller
                        name="tracker_id"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                            <Select
                                labelId="tracker-label"
                                label="Tracker"
                                error={!!error}
                                {...field}
                                sx={{
                                    height: { xs: "48px", md: "56px" },
                                    fontSize: { xs: "16px", md: "18px" },
                                }}
                            >
                                {trackers?.map((tracker) => (
                                    <MenuItem
                                        key={tracker?.id}
                                        value={tracker?.id}
                                    >
                                        {tracker?.tracker_name}
                                    </MenuItem>
                                ))}
                            </Select>
                        )}
                    />
                    <FormHelperText
                        sx={{
                            fontSize: { xs: "12px", md: "14px" },
                            mt: { xs: 0.5, md: 1 },
                        }}
                    >
                        {errors.tracker_id?.message ||
                            "Select the tracker you want to associate this landing page with."}
                    </FormHelperText>
                </FormControl>
            </Box>
        );
    };

    const renderCustomSourcesLoading = () =>
        customSources?.isLoadingList &&
        isLeadOrFunnelTemplate && (
            <Box width="100%">
                <Skeleton
                    variant="rectangular"
                    height={56}
                    sx={{ borderRadius: 1 }}
                />
            </Box>
        );

    const renderCustomSourcesSelect = () =>
        !!customSources?.data?.length &&
        !customSources?.isLoadingList &&
        isLeadOrFunnelTemplate && (
            <Box>
                <Typography
                    variant="body1"
                    sx={{
                        fontWeight: 600,
                        mb: { xs: 1, md: 1.5 },
                        fontSize: { xs: "16px", md: "18px" },
                    }}
                >
                    Lead Custom Source
                </Typography>
                <Controller
                    control={control}
                    name="lead_source_api_id"
                    render={({
                        field: { onChange, name, value },
                        fieldState: { error },
                    }) => (
                        <FormControl fullWidth error={!!error}>
                            <Select
                                name={name}
                                value={value}
                                label="Lead Custom Source"
                                onChange={onChange}
                                error={!!error}
                                required
                                sx={{
                                    height: { xs: "48px", md: "56px" },
                                    fontSize: { xs: "16px", md: "18px" },
                                }}
                            >
                                {customSources?.data?.map((source) => (
                                    <MenuItem
                                        key={source?.id}
                                        value={source?.id}
                                    >
                                        <Stack
                                            direction="row"
                                            justifyContent="space-between"
                                            alignItems="center"
                                            width="100%"
                                            spacing={1}
                                        >
                                            <Typography
                                                sx={{
                                                    fontSize: {
                                                        xs: "14px",
                                                        md: "16px",
                                                    },
                                                }}
                                            >
                                                {source?.name}
                                            </Typography>
                                            <Chip
                                                label={
                                                    source?.has_connection
                                                        ? "Has connection"
                                                        : "Has no connections"
                                                }
                                                color={
                                                    source?.has_connection
                                                        ? "success"
                                                        : "error"
                                                }
                                                size="small"
                                                sx={{
                                                    height: "24px",
                                                    fontSize: {
                                                        xs: "11px",
                                                        md: "12px",
                                                    },
                                                }}
                                                onClick={() => {}}
                                            />
                                        </Stack>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                />
            </Box>
        );

    const renderNoCustomSourcesAlert = () =>
        !customSources?.data?.length &&
        !customSources?.isLoadingList &&
        isLeadOrFunnelTemplate && (
            <Box
                sx={{
                    p: { xs: 2, md: 3 },
                    backgroundColor: "#F8F9FA",
                    borderRadius: 2,
                    border: "1px solid #EDF0F6",
                }}
            >
                <Stack gap={2} alignItems="center">
                    <Alert severity="info" sx={{ width: "100%" }}>
                        <Typography variant="body2">
                            No available source api to select. Please create a
                            Source API.
                        </Typography>
                    </Alert>
                    <Button
                        color="primary"
                        variant="contained"
                        onClick={() => navigate("/leads-wizard")}
                    >
                        Create Source API
                    </Button>
                </Stack>
            </Box>
        );

    const renderGamificationLoading = () =>
        gamificationTemplates?.isLoadingList &&
        isGamificationTemplate && (
            <Box width="100%">
                <Skeleton
                    variant="rectangular"
                    height={56}
                    sx={{ borderRadius: 1 }}
                />
            </Box>
        );

    const renderGamificationSelect = () =>
        gamificationTemplates?.data &&
        !gamificationTemplates?.isLoadingList &&
        isGamificationTemplate && (
            <Box>
                <Controller
                    control={control}
                    name="gamification_type"
                    render={({
                        field: { onChange, name, value },
                        fieldState: { error },
                    }) => (
                        <FormControl fullWidth error={!!error}>
                            <Select
                                name={name}
                                value={value}
                                label="Gamification Template"
                                onChange={onChange}
                                error={!!error}
                                required
                                sx={{
                                    height: { xs: "48px", md: "56px" },
                                    fontSize: { xs: "16px", md: "18px" },
                                }}
                            >
                                {gamificationTemplates?.data?.map(
                                    (template) => (
                                        <MenuItem
                                            key={template?.type}
                                            value={template?.type}
                                        >
                                            <Typography
                                                sx={{
                                                    fontSize: {
                                                        xs: "14px",
                                                        md: "16px",
                                                    },
                                                }}
                                            >
                                                {template?.description}
                                            </Typography>
                                        </MenuItem>
                                    ),
                                )}
                            </Select>
                        </FormControl>
                    )}
                />
            </Box>
        );

    // ========================================================================
    // Render
    // ========================================================================

    return (
        <StepContainer
            handleSubmit={handleSubmit}
            onSubmit={onSubmit}
            isDirty={isDirty}
        >
            {/* Step Header */}
            <Box sx={{ mb: { xs: 3, md: 2 } }}>
                <Typography
                    variant="h4"
                    sx={{
                        fontWeight: 600,
                        fontSize: { xs: "24px", sm: "28px", md: "32px" },
                        mb: { xs: 1, md: 2 },
                        textAlign: { xs: "center", md: "left" },
                    }}
                >
                    Connections & Integrations
                </Typography>
                <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{
                        fontSize: { xs: "14px", sm: "16px" },
                        textAlign: { xs: "center", md: "left" },
                        lineHeight: 1.5,
                    }}
                >
                    Connect your landing page with tracking tools and app stores
                </Typography>
            </Box>

            {/* Form Fields */}
            <Stack spacing={{ xs: 3, md: 4 }} sx={{ width: "100%" }}>
                {/* Tracker Selection */}
                {renderTrackerSelect()}

                {/* Custom Sources */}
                {renderCustomSourcesLoading()}
                {renderCustomSourcesSelect()}
                {renderNoCustomSourcesAlert()}

                {/* Gamification Templates */}
                {renderGamificationLoading()}
                {renderGamificationSelect()}
                {/* Store Type */}
                {renderTemplateInput({
                    hookForm,
                    field: {
                        name: "app_type",
                        description: "Store Type",
                        type: "multi_select",
                        attributes: [],
                        options: STORE_TYPE_OPTIONS,
                    },
                    selectedAppTypes,
                    errors,
                    appleIdField,
                    dispatchGetAppDetails,
                    appDetails,
                })}

                {/* MMP Provider */}
                {renderTemplateInput({
                    hookForm,
                    field: {
                        name: "mmp",
                        description: "MMP Provider",
                        type: "combobox",
                        options: MMP_PROVIDER_OPTIONS,
                        tooltip:
                            "If you are using an MMP (Mobile Measurement Partner) provider such as Appsflyer or Adjust, this platform should be selected. MMP providers are used to track and measure the performance of your mobile applications.",
                    },
                    mmpField,
                    errors,
                    selectedAppTypes,
                    marketplaceInstalled:
                        populatedTracker?.marketplace_installed,
                    oneLinkValidation,
                })}

                {/* Target Link for Web */}
                {selectedAppTypes?.includes("web") &&
                    renderTemplateInput({
                        hookForm,
                        field: {
                            id: 13,
                            name: "target_link",
                            description: "Target Url",
                            type: "input",
                            attributes: [],
                            options: [],
                            default_value: null,
                            tooltip:
                                "Provide the target URL to redirect users to a specific page seamlessly.",
                            required: false,
                        },
                        selectedAppTypes,
                        errors,
                        appleIdField,
                        dispatchGetAppDetails,
                        appDetails,
                    })}
            </Stack>
        </StepContainer>
    );
}
